import {
	CombinedEventDto,
	isHoldingEventDto,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingDetails/types.ts';
import { isSecurityIssuedInUnits } from '@dealstack/frontend/shared/cap.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { constructTransactionSentence } from '@dealstack/frontend/shared/utils/constructTransactionSentence.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	DATE_FORMATS,
	parseBEDateIgnoreTimezone,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { ActionIcon, Flex } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface Props {
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>;
}

export const getHoldingDetailsColumns: (props: Props) => ExtendedColumnDef<CombinedEventDto>[] = ({
	tCapManage,
}) => [
	createDateColumn({
		id: 'date',
		dateFormat: DATE_FORMATS.CALENDAR_DATE,
		headerText: tCapManage('holding.details.table.columns.date'),
		accessorKey: 'date',
		// Render the calendar day the BE stored, not the viewer-local day (dates
		// are date-only @db.Date values at UTC midnight — plain new Date() shifts
		// them a day west of UTC).
		accessorFn: (row) => (row.date ? parseBEDateIgnoreTimezone(row.date) : null),
		maxSize: 300,
	}),
	createTextColumn({
		id: 'description',
		headerText: tCapManage('holding.details.table.columns.description'),
		accessorFn: (originalRow: CombinedEventDto) => {
			if (isHoldingEventDto(originalRow)) {
				return constructTransactionSentence({
					transactionType: originalRow.type ?? undefined,
					units: originalRow.units,
					securityUnitDenominator: originalRow.securityUnitDenominator,
					transactionDate: originalRow.date,
					buyerName: originalRow.destDisplayName,
					sellerName: originalRow.sourceDisplayName,
					securityName: originalRow.securityName,
					securityCurrency: originalRow.securityCurrency,
					securityIssuedIn: originalRow.securityIssuedIn,
					cost: originalRow.transactionCost ?? 0,
					preEventHoldingUnits: originalRow.preEventHoldingUnits,
					postEventHoldingUnits: originalRow.postEventHoldingUnits,
					preEventSecurityName: originalRow.preEventSecurityName,
				});
			}

			return originalRow.note || originalRow.description || '';
		},
	}),
	{
		id: 'expand-row',
		header: '',
		cell: ({ row }) => {
			if (
				isHoldingEventDto(row.original) &&
				isSecurityIssuedInUnits(row.original.securityIssuedIn) &&
				row.original.blocksMoving.length
			) {
				return (
					<Flex align="center" justify="flex-end" w="100%" pr="0.5rem">
						<ActionIcon
							onClick={(e) => {
								e.stopPropagation();
								row.toggleExpanded();
							}}
						>
							<Icon
								variant={row.getIsExpanded() ? 'caret-up' : 'caret-down'}
								size="lg"
							/>
						</ActionIcon>
					</Flex>
				);
			}
			return null;
		},
		minSize: 40,
		maxSize: 80,
	},
];
